import { Dropdown } from "react-bootstrap";
import { Flag } from "../WatchBlock.tsx";
import { toWorker } from "../../util/toWorker.ts";
import { useLocalPartial } from "../../util/local.ts";
import i18n from "../../util/i18n.ts";
import {
	useCallback,
	useEffect,
	useState,
	type ReactNode,
	type RefObject,
} from "react";
import type { SelectedRows } from "./useBulkSelectRows.ts";
import { watchListDialog } from "./watchListDialog.tsx";
import type { Props } from "./index.tsx";

export type BulkAction = {
	onClick: (selectedRows: SelectedRows) => void;
	text: ReactNode;
	textLong?: ReactNode;
};

export const BulkActions = ({
	extraActions,
	hasTitle,
	hideAllControls,
	name,
	selectedRows,
	wrapperRef,
}: {
	extraActions: BulkAction[] | undefined;
	hasTitle: boolean;
	hideAllControls: Props["hideAllControls"];
	name: string;
	selectedRows: SelectedRows;
	wrapperRef: RefObject<HTMLDivElement | null>;
}) => {
	const { numWatchColors } = useLocalPartial(["numWatchColors"]);

	const numExtraActions = extraActions?.length ?? 0;

	const getUpdatedShowInlineButtons = useCallback(() => {
		// Never show inline if there's a title, because there's no room!
		if (hasTitle || !wrapperRef.current) {
			return false;
		}

		// Cutoff for when there is enough room to show inline buttons - changes when more buttons are shown or more space is available
		let baseCutoff = 460;

		// Assume 80 pixels per button
		baseCutoff += numExtraActions * 80;

		if (hideAllControls) {
			baseCutoff -= 220;
		}

		return wrapperRef.current.offsetWidth >= baseCutoff;
	}, [hasTitle, hideAllControls, numExtraActions, wrapperRef]);

	const [showInlineButtons, setShowInlineButtons] = useState(false);

	useEffect(() => {
		if (wrapperRef.current) {
			getUpdatedShowInlineButtons();

			const update = () => {
				setShowInlineButtons(getUpdatedShowInlineButtons);
			};

			const resizeObserver = new ResizeObserver(update);
			resizeObserver.observe(wrapperRef.current);

			return () => {
				resizeObserver.disconnect();
			};
		}
	}, [getUpdatedShowInlineButtons, wrapperRef]);

	const hasSomeSelected = selectedRows.map.size > 0;

	const onWatchPlayers = async () => {
		const pids = Array.from(selectedRows.map.values())
			.filter((metadata) => metadata.type === "player")
			.map((metadata) => {
				return metadata.pid;
			});

		if (numWatchColors <= 1) {
			// Toggle watch colors
			await toWorker("main", "updatePlayersWatch", { pids });
		} else {
			// Show popup to select colors
			const watch = await watchListDialog({
				numPlayers: selectedRows.map.size,
				numWatchColors,
			});
			if (watch !== null) {
				await toWorker("main", "updatePlayersWatch", { pids, watch });
			}
		}
	};

	const actions: BulkAction[] = [
		{
			onClick: onWatchPlayers,
			text: (
				<>
					{i18n.t("bulkActions.watch", { ns: "ui", defaultValue: "Follow" })} <Flag />
				</>
			),
			textLong: (
				<>
					{numWatchColors > 1
						? i18n.t("bulkActions.setFollowMarker", { ns: "ui", defaultValue: "Set follow marker" })
						: i18n.t("bulkActions.toggleFollowMarker", { ns: "ui", defaultValue: "Toggle follow marker" })}{" "}
					<Flag />
				</>
			),
		},
		...(extraActions ?? []),
	];

	if (showInlineButtons) {
		return (
			<div
				className="d-flex align-items-start gap-2 mb-2"
				data-datatable-bulk-actions="inline"
			>
				{actions.map((action, i) => (
					<button
						key={i}
						className="btn btn-sm btn-primary"
						data-datatable-bulk-action={i === 0 ? "watch" : "extra"}
						data-datatable-selected-count={selectedRows.map.size}
						disabled={!hasSomeSelected}
						onClick={() => {
							action.onClick(selectedRows);
						}}
					>
						{action.text}
					</button>
				))}
			</div>
		);
	}

	return (
		<>
			<Dropdown className="mb-2" data-datatable-bulk-actions="menu">
				<Dropdown.Toggle
					data-datatable-bulk-actions-toggle=""
					data-datatable-selected-count={selectedRows.map.size}
					id={`datatable-bulk-actions-${name}`}
					size="sm"
					variant="primary"
				>
					{i18n.t("bulkActions.menu", {
						ns: "ui",
						defaultValue: "Bulk actions",
					})}
				</Dropdown.Toggle>
				<Dropdown.Menu data-datatable-bulk-actions-menu="">
					{actions.map((action, i) => (
						<Dropdown.Item
							key={i}
							data-datatable-bulk-action={i === 0 ? "watch" : "extra"}
							onClick={() => {
								action.onClick(selectedRows);
							}}
							disabled={!hasSomeSelected}
						>
							{action.textLong ?? action.text}
						</Dropdown.Item>
					))}
					<Dropdown.Header>
						{i18n.t("bulkActions.selectedCount", {
							ns: "ui",
							defaultValue: "{{count}} players selected",
							count: selectedRows.map.size,
						})}
					</Dropdown.Header>
				</Dropdown.Menu>
			</Dropdown>
		</>
	);
};
